import {
  AlignLeft,
  BookOpenText,
  Building2,
  ChevronDown,
  ChevronUp,
  FileText,
  GripVertical,
  Plus,
  FileImage,
  SlidersHorizontal,
  Table2,
  Trash2,
  UsersRound,
} from "lucide-react";

import {
  sectionSiblingPosition,
  type Manuscript,
} from "../domain/manuscript";

export type ActivePanel =
  | "metadata"
  | "authors"
  | "abstract"
  | "tables"
  | "figures"
  | "references"
  | "format"
  | string;

type OutlinePanelProps = {
  manuscript: Manuscript;
  activeId: ActivePanel;
  onSelect: (id: ActivePanel) => void;
  onAdd: (parentId?: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onDelete: (id: string) => void;
};

export function OutlinePanel({
  manuscript,
  activeId,
  onSelect,
  onAdd,
  onMove,
  onDelete,
}: OutlinePanelProps) {
  return (
    <aside className="outline-panel" aria-label="稿件结构">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">MANUSCRIPT</span>
          <h2>稿件结构</h2>
        </div>
        <span className="count-pill">{manuscript.sections.length + 7}</span>
      </div>

      <nav className="outline-nav">
        <button
          className={`outline-item fixed ${activeId === "metadata" ? "active" : ""}`}
          onClick={() => onSelect("metadata")}
          type="button"
        >
          <UsersRound size={16} />
          <span>
            <strong>题名</strong>
            <small>Title page</small>
          </span>
        </button>
        <button
          className={`outline-item fixed ${activeId === "authors" ? "active" : ""}`}
          onClick={() => onSelect("authors")}
          type="button"
        >
          <Building2 size={16} />
          <span>
            <strong>作者与机构</strong>
            <small>{manuscript.authors.length} authors · {manuscript.affiliations.length} affiliations</small>
          </span>
        </button>
        <button
          className={`outline-item fixed ${activeId === "abstract" ? "active" : ""}`}
          onClick={() => onSelect("abstract")}
          type="button"
        >
          <AlignLeft size={16} />
          <span>
            <strong>摘要与关键词</strong>
            <small>Abstract · Keywords</small>
          </span>
        </button>

        <div className="outline-divider">
          <span>正文区块</span>
          <span>{manuscript.sections.length}</span>
        </div>

        {manuscript.sections.map((section) => {
          const siblingPosition = sectionSiblingPosition(
            manuscript.sections,
            section.id,
          );
          return (
            <div
              className={`outline-row level-${section.level} ${
                activeId === section.id ? "active" : ""
              }`}
              key={section.id}
            >
              <GripVertical className="drag-hint" size={14} />
              <button
                className="outline-section-main"
                onClick={() => onSelect(section.id)}
                type="button"
              >
                <span className="level-badge">H{section.level}</span>
                <span className="outline-title">
                  {section.title || "未命名区块"}
                </span>
              </button>
              <div className="outline-actions">
                {section.level < 3 ? (
                  <button
                    aria-label={`在“${section.title || "未命名区块"}”下添加 H${section.level + 1}`}
                    className="outline-add-child"
                    onClick={() => onAdd(section.id)}
                    title={`添加${section.level === 1 ? "二级" : "三级"}标题`}
                    type="button"
                  >
                    <Plus size={12} />
                  </button>
                ) : null}
                <button
                  aria-label={`上移“${section.title || "未命名区块"}”`}
                  disabled={siblingPosition.index <= 0}
                  onClick={() => onMove(section.id, -1)}
                  type="button"
                >
                  <ChevronUp size={13} />
                </button>
                <button
                  aria-label={`下移“${section.title || "未命名区块"}”`}
                  disabled={
                    siblingPosition.index < 0 ||
                    siblingPosition.index === siblingPosition.count - 1
                  }
                  onClick={() => onMove(section.id, 1)}
                  type="button"
                >
                  <ChevronDown size={13} />
                </button>
                <button
                  aria-label={`删除“${section.title || "未命名区块"}”`}
                  className="danger-icon"
                  onClick={() => onDelete(section.id)}
                  type="button"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}

        <div className="outline-divider">
          <span>文后与版式</span>
          <span>{manuscript.tables.length + manuscript.figures.length + manuscript.references.length}</span>
        </div>
        <button
          className={`outline-item fixed ${activeId === "tables" ? "active" : ""}`}
          onClick={() => onSelect("tables")}
          type="button"
        >
          <Table2 size={16} />
          <span>
            <strong>三线表</strong>
            <small>{manuscript.tables.length} editable tables</small>
          </span>
        </button>
        <button
          className={`outline-item fixed ${activeId === "figures" ? "active" : ""}`}
          onClick={() => onSelect("figures")}
          type="button"
        >
          <FileImage size={16} />
          <span>
            <strong>图注</strong>
            <small>{manuscript.figures.length} figure legends</small>
          </span>
        </button>
        <button
          className={`outline-item fixed ${activeId === "references" ? "active" : ""}`}
          onClick={() => onSelect("references")}
          type="button"
        >
          <BookOpenText size={16} />
          <span>
            <strong>参考文献</strong>
            <small>{manuscript.references.length} references</small>
          </span>
        </button>
        <button
          className={`outline-item fixed ${activeId === "format" ? "active" : ""}`}
          onClick={() => onSelect("format")}
          type="button"
        >
          <SlidersHorizontal size={16} />
          <span>
            <strong>格式要求</strong>
            <small>Fonts · Page · Paragraph</small>
          </span>
        </button>
      </nav>

      <div className="outline-add">
        <p>添加一级标题</p>
        <div>
          <button
            aria-label="添加 H1 一级章节"
            onClick={() => onAdd()}
            title="添加自定义一级章节"
            type="button"
          >
            <Plus size={13} /> H1
          </button>
        </div>
      </div>

      <div className="outline-note">
        <FileText size={15} />
        <p>
          一级标题可直接写正文，也可留空；点击标题行右侧“＋”添加它所属的 H2，再在 H2 下添加 H3。
        </p>
      </div>
    </aside>
  );
}
